import { trpc } from '@/utils/trpc';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { updatePassword } from 'aws-amplify/auth';
import { toast } from 'sonner';
import { useNavigate } from 'react-router';
import { useEffect } from 'react';
import {
  AUTH_PATHS,
  AUTH_SESSION_KEYS,
} from '@/feature/authentication/constants';
import { useAuth } from '@/routes/AuthContext';
import { savePostLoginRedirect } from '@/feature/authentication/utils/postLoginRedirect';

type UseGetCurrentUserProfileOptions = {
  enabled?: boolean;
};

export const getHttpStatus = (error: unknown) =>
  (error as { data?: { httpStatus?: number } } | null)?.data?.httpStatus;

// Hook for updating user email
export const useUpdateEmail = () => {
  return useMutation(trpc.usersRouter.updateEmailAddress.mutationOptions());
};

// Hook for updating user name
export const useUpdateName = () => {
  return useMutation(trpc.userSettingsRouter.updateNames.mutationOptions());
};

// Hook for updating user password using AWS Amplify
export const useUpdatePassword = () => {
  return useMutation({
    mutationFn: async (data: {
      currentPassword: string;
      newPassword: string;
    }) => {
      try {
        await updatePassword({
          oldPassword: data.currentPassword,
          newPassword: data.newPassword,
        });
        return {
          success: true,
          message: 'Password updated successfully',
        };
      } catch (error: unknown) {
        const amplifyError = error as { name?: string; message?: string };
        // consolidate all the error messages into general message.
        if (
          amplifyError.name === 'NotAuthorizedException' ||
          amplifyError.name === 'InvalidPasswordException' ||
          amplifyError.name === 'LimitExceededException'
        ) {
          throw new Error('Password is incorrect.');
        }
        throw new Error(amplifyError.message || 'Failed to update password');
      }
    },
  });
};

// Hook for getting current user profile
export const useGetCurrentUserProfile = (
  options: UseGetCurrentUserProfileOptions = {},
) => {
  const navigate = useNavigate();
  const { handleSignOut } = useAuth();

  const queryResult = useQuery({
    ...trpc.userSettingsRouter.getCurrentUser.queryOptions(),
    enabled: options.enabled ?? true,
    retry: (failureCount, error) => {
      const errorStatus = getHttpStatus(error);
      if (errorStatus === 401 || errorStatus === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });

  useEffect(() => {
    const errorStatus = getHttpStatus(queryResult.error);
    const isForbidden = errorStatus === 403;
    const isExpiredSession =
      errorStatus === 401 || (errorStatus != null && errorStatus >= 500);

    if (!isForbidden && !isExpiredSession) {
      return;
    }

    const handleError = async () => {
      if (isForbidden) {
        navigate(AUTH_PATHS.NO_ACCESS, { replace: true });
        return;
      }

      sessionStorage.removeItem(AUTH_SESSION_KEYS.NEW_PASSWORD_REQUIRED);
      savePostLoginRedirect(
        `${window.location.pathname}${window.location.search}`,
      );
      toast.error('Your session has expired. Please log in again.');
      await handleSignOut({ preservePostLoginRedirect: true });
      navigate(AUTH_PATHS.LOGIN, { replace: true });
    };

    void handleError();
  }, [handleSignOut, navigate, queryResult.error]);

  return queryResult;
};

// Hook to invalidate user profile queries
export const useInvalidateUserProfile = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({
      queryKey: trpc.userSettingsRouter.getCurrentUser.queryKey(),
    });
  };
};
